"use client";

import type { InputHTMLAttributes, ReactNode } from "react";

/**
 * The Apply Now form's building blocks.
 *
 * The source composes shadcn `Form` / `Input` / `Button` / `Select`. This
 * project has no shadcn primitives, so the classes those components ACTUALLY
 * render — base classes plus the call site's overrides after `twMerge` — are
 * written out here once and reused across the four steps.
 *
 * Resolved from the source's ui/*.tsx:
 *   FormItem    → `space-y-2`
 *   FormLabel   → `text-sm font-medium leading-none` + the call site's
 *                 `font-normal text-sm lg:text-base text-[#737373]`, so the
 *                 weight and colour below are the ones that win.
 *   FormMessage → `text-[0.8rem] font-medium text-red-500` + `text-[#E53935]`
 *   Input       → `flex h-9 w-full rounded-md border border-[#BEBEBE]
 *                  bg-transparent px-3 py-1 text-sm shadow-sm transition-colors
 *                  placeholder:text-[#BEBEBE] focus-visible:ring-1
 *                  focus-visible:ring-slate-950 disabled:opacity-50`
 *
 * Breakpoints are translated per `@/widgets/internship/container`: the source's
 * `lg:` is 1024px, which is `min-[1024px]:` here (this project remaps `lg:` to
 * 1430px). Tailwind v3 `shadow-sm` is v4 `shadow-xs`.
 */

/** Source `FormLabel` as the call sites configure it. */
export const FIELD_LABEL_CLASSNAME =
  "block font-normal text-sm min-[1024px]:text-base leading-none text-[#737373]";

/** Source `FormMessage` + its `text-[#E53935]` override. */
export const FIELD_MESSAGE_CLASSNAME =
  "text-[0.8rem] font-medium text-[#E53935]";

/** Source shadcn `Input`, with `border-gray-300` where the call site sets it. */
export const INPUT_CLASSNAME =
  "flex h-9 w-full rounded-md border border-[#BEBEBE] bg-transparent px-3 py-1 " +
  "text-sm shadow-xs transition-colors placeholder:text-[#BEBEBE] " +
  "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 " +
  "disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Source `Button` size `default` = `h-7 px-5 py-2`, but every button in this
 * form overrides the padding with `p-5`, which beats `px-5 py-2` in `twMerge`
 * while `h-7` still caps the height. Kept as-is so the buttons match.
 */
const BUTTON_BASE =
  "inline-flex items-center justify-center whitespace-nowrap rounded-md " +
  "h-7 p-5 font-medium transition-colors shadow-sm " +
  "disabled:pointer-events-none disabled:opacity-50";

/** `bg-[#9F1C33] hover:bg-[#8a1829] text-white` — the primary action. */
export const PRIMARY_BUTTON_CLASSNAME =
  `${BUTTON_BASE} bg-[#9F1C33] hover:bg-[#8a1829] text-white`;

/** `bg-gray-400 hover:bg-gray-500 text-white border-0` — the Back action. */
export const SECONDARY_BUTTON_CLASSNAME =
  `${BUTTON_BASE} bg-gray-400 hover:bg-gray-500 text-white border-0`;

interface FieldProps {
  label: ReactNode;
  /** Rendered under the control; the source colours it #E53935. */
  error?: string;
  children: ReactNode;
  /** The source's date field uses `FormItem className="flex flex-col"`. */
  className?: string;
  htmlFor?: string;
}

/** Source `FormItem` + `FormLabel` + `FormMessage`, as one unit. */
export function Field({
  label,
  error,
  children,
  className = "",
  htmlFor,
}: FieldProps) {
  return (
    <div className={`space-y-2 ${className}`.trim()}>
      <label className={FIELD_LABEL_CLASSNAME} htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? <p className={FIELD_MESSAGE_CLASSNAME}>{error}</p> : null}
    </div>
  );
}

/** Marks a field the source labels with a red asterisk (step 2 only). */
export function RequiredMark() {
  return <span className="text-red-500">*</span>;
}

export type TextFieldProps = InputHTMLAttributes<HTMLInputElement>;

/** Source shadcn `Input`. */
export function TextField({ className = "", ...props }: TextFieldProps) {
  return <input className={`${INPUT_CLASSNAME} ${className}`.trim()} {...props} />;
}
